import merge from 'lodash/merge';
import { slideDown, slideUp } from '@mindkomm/slide';
import { forEach } from '@mindkomm/utils';

import { nextAll, prevAll } from './next';
import { findStartLetterInCollection } from './helpers';

/**
 * a11yNavigation
 *
 * Makes a navigation with dropdowns keyboard accessible and and adds better support for assistive technology.
 */
export function a11yNavigation(selector, options) {
  /**
   * Default values.
   */
  var defaults = {
    navItems: '.js-nav-items',
    linksTopLevel: '.js-nav-link-level-top',
    linksSubLevel: '.js-nav-link-level-sub',
    dropdowns: '.js-nav-dropdown',

    /**
     * Whether dropdown handling should be disabled on desktop sizes.
     */
    disableDropdownsOnDesktop: false,

    /**
     * Whether dropdowns should be animated on desktop as well.
     */
    animateDropdownsOnDesktop: false,

    /**
     * A selector for a DOM element which has a cursor property that defines
     * whether mobile styles and functionality should be applied.
     */
    cursorPropertyItem: '.js-menu-open',

    classes: {
      hasDropdown: 'js-nav-link-has-dropdown',
      isActive: 'is-active'
    }
  };

  /**
   * Keyboard key numbers
   */
  var keys = {
    enter: 13,
    esc: 27,
    tab: 9,
    left: 37,
    up: 38,
    right: 39,
    down: 40,
    spacebar: 32
  };

  /**
   * Constructor function for A11yNavigation.
   *
   * @param {string} selector - The selector for the main nav element
   * @param {obj} options
   * @constructor
   */
  function A11yNavigation(selector, options) {
    this.$nav = document.querySelector(selector);
    this.options = merge({}, defaults, options);

    this.el = {
      $navItems: this.$nav.querySelectorAll(this.options.navItems),
      $linksTopLevel: this.$nav.querySelectorAll(this.options.linksTopLevel),
      $linksSubLevel: this.$nav.querySelectorAll(this.options.linksSubLevel),
      $dropdowns: this.$nav.querySelectorAll(this.options.dropdowns),
      $hasDropdown: this.$nav.querySelectorAll(
        '.' + this.options.classes.hasDropdown
      ),
      $cursorPropertyItem: document.querySelector(
        this.options.cursorPropertyItem
      )
    };

    this.listeners = {
      resizeListener: this.resizeListener.bind(this),
      toggleDropdown: this.toggleDropdown.bind(this),
      onFocusTopLevelLink: this.onFocusTopLevelLink.bind(this),
      onFocusSubLevelLink: this.onFocusSubLevelLink.bind(this),
      handleNavLinksLevelTop: this.handleNavLinksLevelTop.bind(this),
      handleNavLinksLevelSub: this.handleNavLinksLevelSub.bind(this),
      closeDropdownWithoutFocus: this.closeDropdownWithoutFocus.bind(this)
    };

    this.events = [
      {
        el: this.el.$hasDropdown,
        handler: 'click',
        listener: this.listeners.toggleDropdown
      },
      {
        el: this.el.$linksTopLevel,
        handler: 'focus',
        listener: this.listeners.onFocusTopLevelLink
      },
      {
        el: this.el.$linksSubLevel,
        handler: 'focus',
        listener: this.listeners.onFocusSubLevelLink
      },
      {
        el: this.el.$linksTopLevel,
        handler: 'keydown',
        listener: this.listeners.handleNavLinksLevelTop
      },
      {
        el: this.el.$linksSubLevel,
        handler: 'keydown',
        listener: this.listeners.handleNavLinksLevelSub
      }
    ];

    /**
     * Storage for dropdown states
     * @type {obj}
     */
    this.dropdowns = {};

    /**
     * Storage for current indices of focused elements
     */
    this.index = {
      topLevel: null,
      subLevel: null
    };

    /**
     * The amount of top level links
     */
    this.topLevelLength = this.el.$linksTopLevel.length;

    /**
     * Storage for the current dropdown object
     * @type {null}
     */
    this.currentDropdownLinks = null;

    this.belowBreakpointMobile = false;

    this.resizeTimer = null;

    this.isBound = false;
  }

  A11yNavigation.prototype = {
    init: function() {
      this.bindEvents();
      this.addAriaRoles();
      this.setupDropdowns();

      this.isBound = true;

      // Call resizeHandler once.
      this.resizeHandler();
    },

    destroy: function() {
      if (!this.isBound) {
        return;
      }

      this.unbindEvents();
      this.removeAriaRoles();
      this.destroyDropdowns();
      this.clearResizeListener();

      this.isBound = false;
    },

    resizeListener: function() {
      this.clearResizeListener();
      this.resizeTimer = setTimeout(this.resizeHandler.bind(this), 100);
    },

    clearResizeListener: function() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    },

    bindEvents: function() {
      window.addEventListener('resize', this.listeners.resizeListener);

      forEach(
        this.events,
        function(event) {
          forEach(event.el, function(el) {
            el.addEventListener(event.handler, event.listener);
          });
        }.bind(this)
      );
    },

    unbindEvents: function() {
      window.removeEventListener('resize', this.listeners.resizeListener);

      forEach(
        this.events,
        function(event) {
          forEach(event.el, function(el) {
            el.removeEventListener(event.handler, event.listener);
          });
        }.bind(this)
      );

      this.unbindCloseDropdownsOnDocumentClick();
    },

    addAriaRoles: function() {
      forEach(
        this.el.$linksTopLevel,
        function(el) {
          // Bail out if it is no link with a dropdown
          if (!el.classList.contains(this.options.classes.hasDropdown)) {
            return true;
          }

          // Add necessary aria-roles
          el.setAttribute(
            'aria-owns',
            'nav-items-' + el.getAttribute('data-id')
          );

          if (!el.getAttribute('aria-expanded')) {
            el.setAttribute('aria-expanded', 'false');
          }
        }.bind(this)
      );

      // Add aria roles to dropdowns
      forEach(this.el.$dropdowns, function(el) {
        if (!el.getAttribute('aria-expanded')) {
          el.setAttribute('aria-expanded', 'false');
        }

        if (!el.getAttribute('aria-hidden')) {
          el.setAttribute('aria-hidden', 'true');
        }
      });

      // Add proper tabindex to links inside dropdowns
      forEach(this.el.$linksSubLevel, function(el) {
        el.setAttribute('tabindex', -1);
      });
    },

    removeAriaRoles: function() {
      // Remove aria-roles from top-level links.
      forEach(
        this.el.$linksTopLevel,
        function(el) {
          el.removeAttribute('aria-owns');
          el.removeAttribute('aria-expanded');
          el.removeAttribute('nav-items-' + el.getAttribute('data-id'));
        }.bind(this)
      );

      // Remove aria roles from dropdowns.
      forEach(this.el.$dropdowns, function(el) {
        el.removeAttribute('aria-expanded');
        el.removeAttribute('aria-hidden');
      });

      // Remove tabindex from links inside dropdowns.
      forEach(this.el.$linksSubLevel, function(el) {
        el.removeAttribute('tabindex');
      });
    },

    /**
     * Fill the dropdown states object
     */
    setupDropdowns: function() {
      forEach(
        this.el.$hasDropdown,
        function($link) {
          var id = $link.getAttribute('aria-owns');
          var $dropdown = document.getElementById(id);
          var isActive = false;

          var $children = $dropdown.querySelectorAll(
            this.options.linksSubLevel
          );

          // Check if link should be expanded
          if ($link.getAttribute('aria-expanded') === 'true') {
            isActive = true;
          }

          this.dropdowns[id] = {
            id: id,
            $el: $dropdown,
            $link: $link,
            $children: $children,
            childrenLength: $children.length,
            childIndex: 0,
            isActive: isActive,
            isAnimating: false
          };
        }.bind(this)
      );
    },

    destroyDropdowns: function() {
      this.dropdowns = {};
    },

    resizeHandler: function() {
      if (
        getComputedStyle(this.el.$cursorPropertyItem)['cursor'] !== 'default'
      ) {
        this.belowBreakpointMobile = true;

        /**
         * Update only when in mobile view so that the current menu item
         * can be expanded.
         */
        this.update();
      } else {
        this.belowBreakpointMobile = false;
      }
    },

    /**
     * All clicks on the document close the dropdown
     */
    bindCloseDropdownsOnDocumentClick: function() {
      document.addEventListener(
        'click',
        this.listeners.closeDropdownWithoutFocus
      );
    },

    unbindCloseDropdownsOnDocumentClick: function() {
      document.removeEventListener(
        'click',
        this.listeners.closeDropdownWithoutFocus
      );
    },

    /**
     * Handle top level link focus.
     *
     * Sets proper top and sub level link indices.
     *
     * @param evt
     * @returns {boolean} Pass on event
     */
    onFocusTopLevelLink: function(evt) {
      var el = evt.currentTarget;
      var closest = el.closest('li');

      // Get index of current element
      this.index.topLevel = [].slice
        .call(closest.parentNode.children)
        .indexOf(closest);

      // Make sure subLevel link is not focused on first click of a top element
      this.index.subLevel = null;

      return true;
    },

    /**
     * Handle sub level link focus.
     *
     * Sets proper sub level link indices.
     *
     * @param evt
     * @returns {boolean} Pass on event
     */
    onFocusSubLevelLink: function(evt) {
      var el = evt.currentTarget;
      var closest = el.closest('li');

      this.index.subLevel = [].slice
        .call(closest.parentNode.children)
        .indexOf(closest);

      return true;
    },

    /**
     * Handle keyboard interaction for top level links.
     *
     * @param evt
     * @returns {boolean}
     */
    handleNavLinksLevelTop: function(evt) {
      var el = evt.currentTarget;
      var id = el.getAttribute('aria-owns');

      /**
       * For every key event, return false at the end to
       * prevent propagation of events.
       */
      switch (evt.keyCode) {
        /**
         * Tab presses are deliberately unhandled to not interfere
         * with browser defaults.
         */
        case keys.tab:
          return true;

        /**
         * Open submenu on enter or down key
         */
        case keys.enter:
        case keys.down:
          this.index.subLevel = 0;
          this.setActiveDropdown(id);
          break;

        /**
         * Open currently focused link if an href is present,
         * otherwise just open submenu.
         */
        case keys.spacebar:
          if (!el.classList.contains(this.options.classes.hasDropdown)) {
            window.location.href = el.getAttribute('href');
          } else {
            this.setActiveDropdown(id);
          }
          break;

        /**
         * Move to sibling on the right.
         * If it’s the last item, go to first sibling.
         *
         * This is where you would think to set keys.tab, too. However,
         * this is not necessary, because
         * 1. The default browser behaviour for the tab is already sufficient.
         * 2. It would break tab navigation, because it wouldn’t be possible
         *    to tab out of the navigation once you’re inside.
         */
        case keys.right:
          this.focusNextTopLevelLink();
          break;

        /**
         * Move to sibling on the left.
         * If it’s the first item, go to last sibling.
         */
        case keys.left:
          this.focusPreviousTopLevelLink();
          break;

        /**
         * Select previous link in dropdown
         * If none is selected yet, go to last link in submenu
         */
        case keys.up:
          // Using a negative index makes it start from the end
          this.setActiveDropdown(id);
          this.index.subLevel = -1;
          break;

        case keys.esc:
          this.setActiveDropdown(null);
          this.index.subLevel = 0;
          el.blur();
          break;

        /**
         * Search through top level links
         */
        default:
          // Check next links and bailout if found
          if (
            this.searchNextLinks(el, this.options.linksTopLevel, evt.keyCode)
          ) {
            return true;
          }

          // Check prev links and bailout if found
          if (
            this.searchPrevLinks(el, this.options.linksTopLevel, evt.keyCode)
          ) {
            return true;
          }
      }

      this.update();

      // Prevent bubbling of events
      return false;
    },

    /**
     * Handle keyboard interaction for links inside dropdowns.
     *
     * @param evt
     * @returns {boolean}
     */
    handleNavLinksLevelSub: function(evt) {
      var el = evt.currentTarget;

      switch (evt.keyCode) {
        case keys.tab:
          if (this.index.subLevel === this.currentDropdownLinks.length - 1) {
            this.index.subLevel = null;
            this.setActiveDropdown(null);
            this.update();
          } else {
            this.index.subLevel = this.index.subLevel + 1;
          }

          // Do not stop propagation for tabs keys
          return true;

        case keys.down:
          this.index.subLevel =
            this.index.subLevel === this.currentDropdownLinks.length - 1
              ? 0
              : this.index.subLevel + 1;
          break;

        case keys.up:
          this.index.subLevel =
            this.index.subLevel === 0
              ? this.currentDropdownLinks.length - 1
              : this.index.subLevel - 1;
          break;

        case keys.left:
          this.index.subLevel = null;
          this.focusPreviousTopLevelLink();
          this.setActiveDropdown(null);
          break;

        case keys.right:
          this.index.subLevel = null;
          this.focusNextTopLevelLink();
          this.setActiveDropdown(null);
          break;

        case keys.esc:
          this.index.subLevel = null;
          this.setActiveDropdown(null);
          this.focusTopLevelLink(this.index.topLevel);
          break;

        case keys.spacebar:
        case keys.enter:
          window.location.href = el.getAttribute('href');
          break;

        /**
         * Search through sub level links
         *
         * First check for links after the current and then check for previous links.
         * Searching for the closest could lead to a link not being selectable via search
         * if there are multiple links that start with the same letter.
         */
        default:
          // Check next links and bailout if found
          if (
            this.searchNextLinks(el, this.options.linksSubLevel, evt.keyCode)
          ) {
            return true;
          }

          // Check previous links and bailout if found
          if (
            this.searchPrevLinks(el, this.options.linksSubLevel, evt.keyCode)
          ) {
            return true;
          }
      }

      this.update();
      return false;
    },

    /**
     * Focus a top level link with a given index.
     *
     * @param atIndex Index of the link to focus
     */
    focusTopLevelLink: function(atIndex) {
      forEach(
        this.el.$navItems,
        function(el) {
          el.children[atIndex]
            .querySelector(this.options.linksTopLevel)
            .focus();
        }.bind(this)
      );
    },

    /**
     * Go to the next top level link.
     *
     * Do nothing if we are on the last top level link. This will make
     * the focus advance out of the menu on the following content.
     */
    focusNextTopLevelLink: function() {
      this.index.subLevel = 0;

      if (this.index.topLevel !== this.topLevelLength - 1) {
        this.index.topLevel += 1;
      } else {
        this.index.topLevel = 0;
      }

      this.focusTopLevelLink(this.index.topLevel);
    },

    /**
     * Go to the previous top level link.
     *
     * If the first link is selected, the focus will advance to the
     * last link in the nav.
     */
    focusPreviousTopLevelLink: function() {
      this.index.subLevel = 0;

      if (this.index.topLevel === 0) {
        this.index.topLevel = this.topLevelLength - 1;
      } else {
        this.index.topLevel -= 1;
      }

      this.focusTopLevelLink(this.index.topLevel);
    },

    toggleDropdown: function(evt) {
      var el = evt.currentTarget;
      var id = el.getAttribute('aria-owns');

      // Bail out if dropdowns are disabled on desktop.
      if (
        !this.belowBreakpointMobile &&
        this.options.disableDropdownsOnDesktop
      ) {
        return true;
      }

      // Bail out if animation is in progress.
      if (this.dropdowns[id].isAnimating) {
        evt.preventDefault();
        evt.stopPropagation();
        return false;
      }

      if (el.getAttribute('aria-expanded') === 'false') {
        this.setActiveDropdown(id);
      } else {
        this.setActiveDropdown(null);
      }

      this.update();

      // Prevent default and stop propagation
      evt.preventDefault();
      evt.stopPropagation();
    },

    /**
     * Go through dropdowns array and set the active one.
     * There can always be only one active dropdown.
     *
     * @param {string|null} id The id of the dropdown to set active. Provide
     *                         null as an argument to set all dropdowns inactive.
     */
    setActiveDropdown: function(id) {
      forEach(
        Object.keys(this.dropdowns),
        function(key) {
          this.dropdowns[key].isActive = key === id;
        }.bind(this)
      );
    },

    /**
     * Go through dropdown states and update UI accordingly.
     */
    update: function() {
      forEach(
        Object.keys(this.dropdowns),
        function(key) {
          var dropdown = this.dropdowns[key];

          if (dropdown.isActive) {
            if (dropdown.$el.getAttribute('aria-expanded') === 'false') {
              this.openDropdown(dropdown);
            }

            if (this.index.subLevel !== null) {
              dropdown.$children[this.index.subLevel].focus();
            }
          } else if (dropdown.$el.getAttribute('aria-expanded') === 'true') {
            this.closeDropdown(dropdown);
          }
        }.bind(this)
      );
    },

    openDropdown: function(dropdown) {
      dropdown.$link.setAttribute('aria-expanded', 'true');

      // Make links in submenu tabbable.
      forEach(dropdown.$children, function(dropdown) {
        dropdown.removeAttribute('tabindex');
      });

      this.currentDropdownLinks = dropdown.$children;

      this.animateOpenDropdown(dropdown);

      if (
        !this.belowBreakpointMobile &&
        !this.options.animateDropdownsOnDesktop
      ) {
        this.bindCloseDropdownsOnDocumentClick();
      }
    },

    /**
     * Close dropdowns without focusing any dropdown.
     */
    closeDropdownWithoutFocus: function() {
      this.setActiveDropdown(null);
      this.index.subLevel = null;
      this.update();
    },

    /**
     * Closes the currently openened submenu.
     *
     * @param dropdown
     */
    closeDropdown: function(dropdown) {
      dropdown.$link.setAttribute('aria-expanded', 'false');

      // Make sure links can’t be accessed via tab when the menu is closed
      forEach(
        dropdown.$el.querySelectorAll(this.options.linksSubLevel),
        function(el) {
          el.setAttribute('tabindex', -1);
        }
      );

      this.animateCloseDropdown(dropdown);
      this.unbindCloseDropdownsOnDocumentClick();
    },

    /**
     * Opens a submenu on a link.
     *
     * @param dropdown
     */
    animateOpenDropdown: function(dropdown) {
      if (
        this.belowBreakpointMobile ||
        this.options.animateDropdownsOnDesktop
      ) {
        var newHeight = dropdown.$el.scrollHeight;

        /**
         * When the dropdown is currently hidden, e.g. in a slideout panel, the height will be 0. In that case,
         * the dropdown is not animated, but just shown.
         */
        if (newHeight < 1) {
          this.setDropdownVisible(dropdown.$el);
          return;
        }

        this.setDropdownProperty(dropdown.id, 'isAnimating', true);
        this.setDropdownVisible(dropdown.$el);

        slideDown(
          dropdown.$el,
          function() {
            this.setDropdownProperty(dropdown.id, 'isAnimating', false);
          }.bind(this)
        );
      } else {
        this.setDropdownVisible(dropdown.$el);
      }
    },

    /**
     * Closes dropdown.
     *
     * @param dropdown
     */
    animateCloseDropdown: function(dropdown) {
      if (
        this.belowBreakpointMobile ||
        this.options.animateDropdownsOnDesktop
      ) {
        this.setDropdownProperty(dropdown.id, 'isAnimating', true);

        slideUp(
          dropdown.$el,
          function() {
            this.setDropdownProperty(dropdown.id, 'isAnimating', false);
            this.setDropdownHidden(dropdown.$el);
          }.bind(this)
        );
      } else {
        this.setDropdownHidden(dropdown.$el);
      }
    },

    /**
     * Set a property in the dropdowns array.
     *
     * @param id        Id of the dropdown.
     * @param property  Property to set.
     * @param value     Value to set.
     */
    setDropdownProperty: function(id, property, value) {
      // Dropdowns might have been destroyed in the meantime.
      if (!this.dropdowns[id]) {
        return;
      }

      this.dropdowns[id][property] = value;
    },

    /**
     * Set required element attributes for when a
     * dropdown is visible.
     *
     * @param $dropdown
     */
    setDropdownVisible: function($dropdown) {
      $dropdown.setAttribute('aria-expanded', 'true');
      $dropdown.setAttribute('aria-hidden', 'false');
      $dropdown.classList.add(this.options.classes.isActive);
    },

    /**
     * Set required element attributes for when a
     * dropdown is not visible.
     *
     * @param $dropdown
     */
    setDropdownHidden: function($dropdown) {
      $dropdown.setAttribute('aria-expanded', 'false');
      $dropdown.setAttribute('aria-hidden', 'true');
      $dropdown.classList.remove(this.options.classes.isActive);
      $dropdown.style.height = '';
    },

    searchNextLinks: function($el, selector, keyCode) {
      var next = nextAll($el.closest('li'));

      return findStartLetterInCollection(next, selector, keyCode);
    },

    searchPrevLinks: function($el, selector, keyCode) {
      var prev = prevAll($el.closest('li'));

      return findStartLetterInCollection(prev, selector, keyCode);
    }
  };

  return new A11yNavigation(selector, options);
}
